import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import classNames from 'classnames';
import moment from 'moment';
import { format } from 'date-fns';

import { LogView } from 'src/components/LogView/LogView';

import ArrowIcon from 'src/assets/layout/navigation/arrow.svg';
import CertifiedIcon from 'src/assets/hosLogs/hosLog/certified.svg';
import ViolationsIcon from 'src/assets/hosLogs/hosLog/violations.svg';
// TODO uncomment Admin edits
// import PendingEditIcon from 'src/assets/hosLogs/hosLog/pendingEdit.svg';
// import MissingIcon from 'src/assets/hosLogs/hosLog/missing.svg';
// TODO uncomment Admin edits
import { millisecondsToHours } from 'src/utils/dateUtils';
import { computeStatusGrid } from 'src/components/LogView/utils';
import { HosLog } from 'src/features/hosLogs/types';

import './hosLogsItem.scss';

interface Props {
  item: HosLog;
  resetEmptyRangeFilter?: () => void;
}

export const HosLogsItem: React.FC<Props> = ({
  item: {
    date,
    totalTimeOnDuty,
    totalDriveTime,
    certifiedStatus,
    violationCount,
    exceptions,
    timelineData,
    timeZone,
    ruleset,
  },
  resetEmptyRangeFilter,
}) => {
  const [dutyTimeLabel, setDutyTimeLabel] = useState('');
  const [driveTimeLabel, setDriveTimeLabel] = useState('');
  const [totalTimeLabel, setTotalTimeLabel] = useState('00:00');

  const dateObj = useMemo(() => moment(date).toDate(), [date]);
  const logDate = useMemo(() => format(dateObj, 'EEE - MMM dd, yyyy'), [dateObj]);
  const isSameDay = useMemo(() => moment(dateObj).isSame(moment(), 'day'), [dateObj]);

  useEffect(() => {
    setDutyTimeLabel(millisecondsToHours(totalTimeOnDuty));
    setDriveTimeLabel(millisecondsToHours(totalDriveTime));
    if (totalDriveTime && totalTimeOnDuty)
      setTotalTimeLabel(millisecondsToHours(totalTimeOnDuty + totalDriveTime));
  }, [totalTimeOnDuty, totalDriveTime]);

  const { statusGrid, statusTimes } = computeStatusGrid(timelineData, isSameDay, timeZone);

  return (
    <Link
      className={classNames('hos-log-item', { 'is-desktop': !isMobile })}
      to={`/hos-log/${date}`}
      onClick={resetEmptyRangeFilter}
    >
      <div className="hos-log-item__content">
        <div className="hos-log-item__content-desktop-flex">
          <div className="hos-log-item__content__header">
            {logDate}
            {certifiedStatus > 0 && (
              <div className="certified">
                <img className="certified-icon" src={CertifiedIcon} alt="certifyLogIcon" />
                Certified
              </div>
            )}
          </div>
          {isMobile && (
            <div className="hos-log-item__content__time">
              <div className="hos-log-item__content__time--duty">
                ON DUTY: <span className="hos-logs__time">{dutyTimeLabel}</span>
              </div>
              <div className="hos-log-item__content__time--drive">
                DRIVE: <span className="hos-logs__time">{driveTimeLabel}</span>
              </div>
              <div className="hos-log-item__content__time--total">
                TOTAL: <span className="hos-logs__time">{totalTimeLabel}</span>
              </div>
            </div>
          )}
          <div className="hos-logs__alerts">
            {violationCount > 0 && (
              <div className="hos-logs__notification">
                <img
                  className="hos-logs__notification-icon"
                  src={ViolationsIcon}
                  alt="ViolationsIcon"
                />
                Violation
              </div>
            )}
            {exceptions.length > 0 && (
              <div className="hos-logs__notification">
                <img
                  className="hos-logs__notification-icon"
                  src={ViolationsIcon}
                  alt="ViolationsIcon"
                />
                Exceptions
              </div>
            )}
            {certifiedStatus === 0 && ruleset !== 'EXEMPT_DRIVER' && (
              <div className="hos-logs__notification">
                <img
                  className="hos-logs__notification-icon"
                  src={ViolationsIcon}
                  alt="ViolationsIcon"
                />
                Uncertified
              </div>
            )}
            {/* TODO uncomment Admin edits */}
            {/* {editActivity && editActivity.status !== 'approved' && (*/}
            {/*  <div className="hos-logs__notification">*/}
            {/*    <img*/}
            {/*      className="hos-logs__notification-icon"*/}
            {/*      src={PendingEditIcon}*/}
            {/*      alt="PendingEditIcon"*/}
            {/*    />*/}
            {/*    Pending Edit*/}
            {/*  </div>*/}
            {/* )}*/}
            {/* {editActivity && (*/}
            {/*  <div className="hos-logs__notification">*/}
            {/*    <img className="hos-logs__notification-icon" src={MissingIcon} alt="MissingIcon" />*/}
            {/*    Missing Info*/}
            {/*  </div>*/}
            {/* )}*/}
            {/* TODO uncomment Admin edits */}
          </div>
        </div>
        {!isMobile && (
          <LogView
            logStartTimeISO={timelineData.logStartTimeISO}
            logLengthHours={timelineData.logLengthHours}
            date={date}
            isOnSameDay={true}
            statusTimes={statusTimes}
            statusGrid={statusGrid}
            timeZone={timeZone}
          />
        )}
      </div>
      <img className="arrow-icon" src={ArrowIcon} alt="ArrowIcon" />
    </Link>
  );
};
